import React, { useMemo, useState } from "react";
import {
  ComposedChart,
  Line,
  Scatter,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
} from "recharts";

/**
 * Overlay chart for Candle Logs.
 * Draws the close price line + colored scatter markers for each bar's
 * evaluation result. Lets the user visually spot near-miss rejections.
 */
const CHART_MARGIN = { top: 5, right: 10, left: 0, bottom: 0 };
const AXIS_TICK = { fontSize: 10, fill: "#94a3b8" };
const Y_DOMAIN = ["dataMin - 20", "dataMax + 20"];
const LEGEND_WRAPPER = { fontSize: 10, color: "#94a3b8" };

const RESULT_COLOR = {
  ACCEPTED: "#10b981", // emerald
  ML_GATE_REJECTED: "#f59e0b", // amber
  NO_LIQUID_OPTION: "#ef4444", // red
  POST_QUOTE_REJECTED: "#ef4444",
  INSUFFICIENT_OPTION_ATR: "#ef4444",
  CONDITIONS_NOT_MET: "#475569", // slate
  PLAN_ENTRY_REJECTED: "#a855f7", // purple
};

const REASON_LABELS = {
  ACCEPTED: "Accepted",
  ML_GATE_REJECTED: "ML Gate",
  NO_LIQUID_OPTION: "No liquid option",
  POST_QUOTE_REJECTED: "Post-quote reject",
  INSUFFICIENT_OPTION_ATR: "ATR warm-up",
  CONDITIONS_NOT_MET: "Conditions not met",
  PLAN_ENTRY_REJECTED: "Plan-entry reject",
};

function bucketOf(row) {
  const ev = row.evaluation || {};
  if (ev.accepted) return "ACCEPTED";
  return ev.rejection_code || "CONDITIONS_NOT_MET";
}

export default function CandleLogChart({ rows }) {
  const [chartUnderlying, setChartUnderlying] = useState("NIFTY");

  const { series, categories } = useMemo(() => {
    const filtered = rows.filter((r) => r.underlying === chartUnderlying);
    const sorted = [...filtered].sort((a, b) =>
      String(a.candle_timestamp).localeCompare(String(b.candle_timestamp)),
    );
    const cats = new Set();
    const s = sorted.map((r) => {
      const bucket = bucketOf(r);
      cats.add(bucket);
      const t = String(r.candle_timestamp).slice(11, 16);
      const close = r.indicators?.close ?? r.ohlcv?.close ?? null;
      return {
        time: t,
        close,
        // A field for each bucket so Recharts can plot separate scatter series
        [bucket]: close,
        raw: r,
      };
    });
    return { series: s, categories: Array.from(cats) };
    // rows content is captured by reference; recomputing on identity change is
    // fine — deep-compare would be wasteful for the >150 rows/day dataset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, chartUnderlying]);

  if (!rows || rows.length === 0) return null;

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    const ev = p.raw?.evaluation || {};
    const ind = p.raw?.indicators || {};
    return (
      <div className="surface rounded-sm p-2 text-[11px] font-mono">
        <div className="text-slate-100 mb-1">
          {p.time} · {chartUnderlying}
        </div>
        <div className="text-slate-400">
          Close: <span className="text-slate-200">{p.close?.toFixed(2)}</span>
        </div>
        <div className="text-slate-400">
          ADX: <span className="text-slate-200">{ind.adx14?.toFixed(1) ?? "—"}</span>
          {" · "}Vol: <span className="text-slate-200">{ind.volume_ratio?.toFixed(2) ?? "—"}</span>
        </div>
        <div className="text-slate-400">
          Result:{" "}
          <span style={{ color: RESULT_COLOR[bucketOf(p.raw)] || "#94a3b8" }}>
            {ev.accepted ? "ACCEPTED" : ev.rejection_code || "—"}
          </span>
        </div>
        {ev.rejection_reason && (
          <div className="text-slate-500 mt-1 max-w-[280px]">{ev.rejection_reason}</div>
        )}
      </div>
    );
  };

  return (
    <div className="surface rounded-sm p-4 mb-4" data-testid="candle-chart">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
          Overlay chart · close price + evaluation markers
        </div>
        <div className="flex gap-1">
          {["NIFTY", "BANKNIFTY"].map((u) => (
            <button
              key={u}
              data-testid={`chart-${u.toLowerCase()}-btn`}
              onClick={() => setChartUnderlying(u)}
              className={`px-3 py-1 text-[10px] uppercase tracking-[0.15em] rounded-sm border transition-colors ${
                chartUnderlying === u
                  ? "border-blue-500 bg-blue-950 text-blue-200"
                  : "border-slate-700 text-slate-400 hover:text-slate-200"
              }`}
            >
              {u}
            </button>
          ))}
        </div>
      </div>

      <div style={{ width: "100%", height: 260 }}>
        <ResponsiveContainer>
          <ComposedChart data={series} margin={CHART_MARGIN}>
            <CartesianGrid stroke="#1e293b" strokeDasharray="2 4" />
            <XAxis
              dataKey="time"
              stroke="#475569"
              tick={AXIS_TICK}
              interval="preserveStartEnd"
            />
            <YAxis
              stroke="#475569"
              tick={AXIS_TICK}
              domain={Y_DOMAIN}
              width={60}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="close"
              stroke="#3b82f6"
              strokeWidth={1.2}
              dot={false}
              isAnimationActive={false}
              name="Close"
            />
            {categories.map((c) => (
              <Scatter
                key={c}
                dataKey={c}
                fill={RESULT_COLOR[c] || "#94a3b8"}
                shape={c === "ACCEPTED" ? "triangle" : "circle"}
                name={REASON_LABELS[c] || c}
                isAnimationActive={false}
              />
            ))}
            <Legend
              iconSize={8}
              wrapperStyle={LEGEND_WRAPPER}
              formatter={(value) => (
                <span style={{ color: "#94a3b8" }}>{value}</span>
              )}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
